import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { IconPlus } from '../icons';
import { NewTicketSheet } from '../create/NewTicketSheet';
import { ReplyDue, TicketStatusBadge, ticketName } from './TicketBits';
import { useNow } from '../ui/useNow';

/**
 * The job page's Tickets card (spec §5): the tickets on this job (`ticket.forJob`), each a link
 * that lands on the Tickets page with its panel open, and "New ticket" with this job's
 * customer and the job itself filled in.
 */
export function TicketsOnJobCard({
  jobId,
  customerId,
  tz,
}: {
  jobId: string;
  customerId: string;
  tz: string;
}) {
  const trpc = useTRPC();
  const q = useQuery(trpc.ticket.forJob.queryOptions({ jobId }));
  const now = useNow();
  const [creating, setCreating] = useState(false);
  const rows = q.data ?? [];
  return (
    <SectionCard
      title={q.data ? `Tickets (${rows.length})` : 'Tickets'}
      right={
        <Button variant="ghost" onClick={() => setCreating(true)}>
          <IconPlus size={14} />
          New ticket
        </Button>
      }
    >
      {q.isPending ? (
        <Skeleton className="h-10 w-full" />
      ) : q.isError ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn&apos;t load this job&apos;s tickets.{' '}
          <button type="button" className="underline" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tickets on this job.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map((t) => (
            <li key={t.id} className="py-2 first:pt-0 last:pb-0">
              <Link
                to="/tickets"
                search={{ ticket: t.id }}
                data-ticket-link={t.id}
                className="flex min-w-0 items-center gap-3 rounded-[var(--radius-sm)] hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{ticketName(t)}</span>
                  <span className="block text-xs">
                    <ReplyDue ticket={t} tz={tz} now={now} />
                  </span>
                </span>
                <TicketStatusBadge status={t.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
      <NewTicketSheet
        open={creating}
        onClose={() => setCreating(false)}
        tz={tz}
        start={{ customerId, jobId }}
      />
    </SectionCard>
  );
}
